import { useCallback, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { deleteTeam, updateTeam } from "../../../../services/apiTeam";
import { useTeams } from "../../../../reactQuery/teams";
import { getResponseData } from "../../../../reactQuery/response";

export function useManageTeams() {
  const { teams, isLoading: isTeamsLoading } = useTeams();
  const queryClient = useQueryClient();
  const [editingTeamId, setEditingTeamId] = useState('');
  const [editedTeamName, setEditedTeamName] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState("");

  const updateTeamMutation = useMutation({
    mutationFn: updateTeam,
    onSuccess: (response) => {
      toast.success(response?.data?.message);
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      setEditingTeamId(null);
      setEditedTeamName("");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
      setEditingTeamId(null);
    },
  });

  const deleteTeamMutation = useMutation({
    mutationFn: deleteTeam,
    onSuccess: (response) => {
      toast.success(response?.data?.message);
      queryClient.invalidateQueries({ queryKey: ["teams"] });
      setIsDeleteDialogOpen(false);
      setSelectedTeamId("");
    },
    onError: (error) => {
      toast.error(error?.response?.data?.message);
      setIsDeleteDialogOpen(false);
      setSelectedTeamId("");
    },
  });

  const teamList = useMemo(
    () =>
      getResponseData(teams, []).filter((team) => Boolean(team?.team_id)),
    [teams]
  );

  const handleEditClick = useCallback((team) => {
    setEditingTeamId(team.team_id);
    setEditedTeamName(team.team);
  }, []);

  const handleUpdateTeam = useCallback(
    (teamId) => {
      if (!editedTeamName.trim()) {
        return;
      }

      updateTeamMutation.mutate({
        teamId,
        data: { team_name: editedTeamName.trim() },
      });
    },
    [editedTeamName, updateTeamMutation]
  );

  const handleTeamNameChange = useCallback((event) => {
    setEditedTeamName(event.target.value);
  }, []);

  const handleKeyPress = useCallback(
    (event, teamId) => {
      if (event.key === "Enter") {
        handleUpdateTeam(teamId);
      }
    },
    [handleUpdateTeam]
  );

  const handleDeleteClick = useCallback((teamId) => {
    setSelectedTeamId(teamId);
    setIsDeleteDialogOpen(true);
  }, []);

  const closeDeleteDialog = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setSelectedTeamId("");
  }, []);

  const submitDelete = useCallback(() => {
    deleteTeamMutation.mutate(selectedTeamId);
  }, [deleteTeamMutation, selectedTeamId]);

  return {
    teamList,
    isTeamsLoading,
    editingTeamId,
    editedTeamName,
    isDeleteDialogOpen,
    handleEditClick,
    handleUpdateTeam,
    handleTeamNameChange,
    handleKeyPress,
    handleDeleteClick,
    closeDeleteDialog,
    submitDelete,
  };
}
